import { XCircle } from "lucide-react";
import React, { useState, useCallback } from "react";
import { useNavigate } from 'react-router-dom';
import { useDeleteV4ManufacturerByRouteManufacturerIdRequisitionAndRequisitionNumberMutation } from 'randmar-api-client';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  useToast
} from 'randmarcomps';
import withManufacturer from "../../../contexts/WithManufacturer";
import Path from '../../../routes/Path';
import InlineSpinner from "../../util/InlineSpinner";

interface IProps {
  requisitionNumber: string;
  applicationId: string;
  fab?: boolean;
  withReload?: boolean;
  onSuccess?: () => void;
  children?: React.ReactNode;
}

const CancelRequisition: React.FC<IProps> = ({ requisitionNumber, applicationId, fab, withReload, onSuccess, children }) => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();
  const [cancelRequisition, { isLoading }] = useDeleteV4ManufacturerByRouteManufacturerIdRequisitionAndRequisitionNumberMutation();

  const handleCancel = useCallback(() => {
    setOpen(false);
  }, []);

  const handleOpen = useCallback(() => {
    setOpen(true);
  }, []);

  const handleConfirm = useCallback(async () => {
    try {
      await cancelRequisition({ routeManufacturerId: applicationId, requisitionNumber }).unwrap();
      toast({ variant: "success", title: "Success", description: "Requisition cancelled successfully." });
      setOpen(false);
      onSuccess?.();
      if (!withReload) {
        navigate(Path.requisitions(applicationId));
      }
    } catch {
      toast({ variant: "destructive", title: "Error", description: "Failed to cancel requisition." });
      setOpen(false);
    }
  }, [applicationId, requisitionNumber, withReload, cancelRequisition, toast, navigate, onSuccess]);

  const fabButton = () => (
    <Button
      variant="ghost"
      size="icon"
      aria-label="remove"
      title={"Cancel draft purchase"}
      onClick={handleOpen}
    >
      <XCircle className="h-6 w-6" />
    </Button>
  );

  const normalButton = () => (
    <div
      title={"Cancel draft purchase"}
      onClick={handleOpen}
      className="cursor-pointer"
    >
      {children}
    </div>
  );

  return (
    <>
      {fab ? fabButton() : normalButton()}

      <Dialog open={open} onOpenChange={(isOpen) => !isOpen && !isLoading && handleCancel()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel Draft Purchase {requisitionNumber}</DialogTitle>
            <DialogDescription>
              Are you sure you want to cancel this draft purchase? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {isLoading ? (
            <div className="flex items-center">
              <InlineSpinner className="mr-2" size="sm" tone="accent" decorative />
              Please wait...
            </div>
          ) : (
            <DialogFooter>
              <Button variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleConfirm}>
                Confirm
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default withManufacturer(CancelRequisition);
